// A map as data: the authored file format (maps/<name>.json) and its validator. The map is point-symmetric
// (180° about the origin, (x, z) -> (-x, -z)): a file holds only the half the author edits — the red side,
// team a's base and everything on its side of the base-to-base diagonal — plus the few self-symmetric
// objects that sit on the centre. expand.ts turns that into the full map.
import type { MonsterKind } from '../monsters.js';
import { expandMap, inAuthoredHalf, mirrorObjects } from './expand.js';
import { distPolyline } from './geometry.js';

export type Pt = [number, number];

export const WALL_STYLES = ['stone', 'palisade', 'hedge'] as const;
export const BUILDING_STYLES = ['house', 'tower', 'chapel', 'mill'] as const;
export const DECOR_KINDS = ['bush', 'grave', 'post', 'barrel'] as const;
export const PROP_KINDS = ['haystack', 'cart', 'cross', 'well'] as const;
export const DEN_KINDS = ['griffin', 'behemoth'] as const satisfies readonly MonsterKind[];
/** How a road is drawn: a cobbled highway, a dirt road, a forest path. Visual only, like the road itself. */
export const ROAD_KINDS = ['tract', 'dirt', 'trail'] as const;
export type WallStyle = typeof WALL_STYLES[number];
export type BuildingStyle = typeof BUILDING_STYLES[number];
export type DecorKind = typeof DECOR_KINDS[number];
export type PropKind = typeof PROP_KINDS[number];
export type RoadKind = typeof ROAD_KINDS[number];

/** Solid polyline: blocks movement and projectiles, not sight — whatever the style. */
export type Wall = { points: Pt[]; style: WallStyle };
/** Oriented rectangle centred on (x, z): w along its local x, d along its local z, rot in radians. Blocks
    movement, projectiles and sight. */
export type Building = { x: number; z: number; w: number; d: number; rot: number; style: BuildingStyle };
/** Water: impassable except over a bridge; sight and projectiles pass. */
export type Stream = { points: Pt[]; width: number };
/** Impassable ground; sight and projectiles pass. closed: a polygon area; open: a ledge line. */
export type Cliff = { points: Pt[]; closed: boolean };
/** Tall grass ellipse (hides whoever stands inside); rx/rz are semi-axes, rot in radians. */
export type Brush = { x: number; z: number; rx: number; rz: number; rot: number };
/** Visual only. No kind draws as 'dirt'; it is left out rather than defaulted, so older files keep their hash. */
export type Road = { points: Pt[]; width: number; kind?: RoadKind };
/** Visual only: blocks nothing. */
export type Decor = { x: number; z: number; kind: DecorKind; rot: number; scale: number };
/** A small obstacle; what each kind blocks is layout.ts's PROP_SHAPES. */
export type Prop = { x: number; z: number; kind: PropKind; rot: number };
/** Oriented rectangle on the ground, the building convention: w along local x, d along local z, rot in radians. */
export type Patch = { x: number; z: number; w: number; d: number; rot: number };

export type MapObjects = {
  camps: Pt[];
  bridges: Pt[];
  trees: Pt[];    // block movement, projectiles and sight
  rocks: Pt[];    // block movement and projectiles
  pillars: Pt[];  // ruin columns: block movement and projectiles
  walls: Wall[];
  buildings: Building[];
  streams: Stream[];
  ponds: Pt[][];  // polygons, water like streams
  cliffs: Cliff[];
  brush: Brush[];
  roads: Road[];
  decor: Decor[];
  props: Prop[];
  fields: Patch[];  // crop rows: visual only
  fords: Patch[];   // shallow crossings: the stream under one is walkable, and slow
};
export const OBJECT_KEYS = ['camps', 'bridges', 'trees', 'rocks', 'pillars', 'walls', 'buildings', 'streams', 'ponds',
  'cliffs', 'brush', 'roads', 'decor', 'props', 'fields', 'fords'] as const satisfies readonly (keyof MapObjects)[];
/* Added after maps were already authored: a file may leave them out, and they load as empty. */
const OPTIONAL_OBJECT_KEYS: readonly (keyof MapObjects)[] = ['props', 'fields', 'fords'];

/** The authored den; its mirror becomes a den of the other kind. */
export type Den = { kind: MonsterKind; pos: Pt };

export type MapData = {
  format: 1;
  name: string;
  /** Full extents in metres; the playable area is |x| <= width/2, |z| <= height/2. */
  width: number;
  height: number;
  pointRadius: number;
  /** Team a's base (with its shop); team b's is the mirror. */
  base: Pt;
  /** Point A (side, mirrored into C) and point B (centre, must be the origin). */
  points: { side: Pt; center: Pt };
  den: Den;
  /** The authored half: every object here also appears mirrored. */
  half: MapObjects;
  /** Self-symmetric objects, emitted once: each collection must equal its own mirror as a set. */
  center: MapObjects;
};

/** Map extents in metres: below the minimum the required objects can't keep their spacing; the maximum
    keeps a hand-edited or uploaded file from making nav and sight grids absurdly large. */
export const MAP_SIZE = { width: { min: 80, max: 400 }, height: { min: 60, max: 400 } } as const;

/** `code` names the problem, `params` fill its sentence: the client words it per language (i18n mapIssue.*),
    core only reports. `place` params are keys of the required-object labels (MapPlace). */
export type MapIssueCode =
  | 'expectedObject' | 'requiredField' | 'unknownField' | 'expectedArray' | 'expectedNumber' | 'mustBePositive'
  | 'expectedPoint' | 'expectedOneOf' | 'expectedBool' | 'pointsCoincide' | 'lineTooShort' | 'polygonTooShort'
  | 'polygonZeroArea' | 'polygonEdgesCross' | 'centerNotSymmetric' | 'outsideMap' | 'baseNearEdge' | 'mirroredHalfPoint'
  | 'noVertexInside' | 'mirroredHalf' | 'mirroredHalfCentroid' | 'duplicates' | 'mirrorDuplicates' | 'duplicatesMirrorOf'
  | 'bridgeNotOnStream' | 'bridgeStreamTooWide' | 'fordMissesStream' | 'fordTooShort' | 'fordTooNarrow' | 'tooClose'
  | 'unsupportedFormat' | 'expectedName' | 'sizeOutOfRange' | 'centerPointNotOrigin'
  // raised outside the schema, by the stores that load and save maps
  | 'error' | 'mapUnreadable' | 'notJson' | 'fileNotJson' | 'fileTooLarge' | 'storageUnavailable' | 'storageFull' | 'storageWriteFailed'
  | 'nameEmpty' | 'nameSpaces' | 'nameTooLong' | 'fileNameInvalid' | 'nameTaken' | 'notFound' | 'devServerDown' | 'serverError' | 'noTemplate';
export type MapPlace = 'base' | 'baseMirror' | 'pointsSide' | 'pointsCenter' | 'pointsSideMirror' | 'denPos' | 'denMirror';
export type MapIssue = { path: string; code: MapIssueCode; params?: Record<string, string | number> };

export class MapValidationError extends Error {
  constructor(readonly source: string, readonly issues: MapIssue[]) {
    super(`invalid map ${source}:\n${issues.map(i => `  ${i.path || '(root)'}: ${i.code}${i.params ? ` ${JSON.stringify(i.params)}` : ''}`).join('\n')}`);
    this.name = 'MapValidationError';
  }
}

/* Tolerance for "the same position" in centre-symmetry checks: authored coordinates come out of float math. */
const SYM_EPS = 1e-6;

type Ctx = MapIssue[];
const join = (path: string, key: string | number) => typeof key === 'number' ? `${path}[${key}]` : path ? `${path}.${key}` : key;
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function record(c: Ctx, v: unknown, path: string, keys: readonly string[], optional: readonly string[] = []): Record<string, unknown> | null {
  if (!isRecord(v)) { c.push({ path, code: 'expectedObject' }); return null; }
  for (const k of keys) if (!(k in v) && !optional.includes(k)) c.push({ path: join(path, k), code: 'requiredField' });
  for (const k of Object.keys(v)) if (!keys.includes(k)) c.push({ path: join(path, k), code: 'unknownField' });
  return v;
}

function list(c: Ctx, v: unknown, path: string): unknown[] {
  if (!Array.isArray(v)) { c.push({ path, code: 'expectedArray' }); return []; }
  return v;
}

function num(c: Ctx, v: unknown, path: string, positive = false): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) { c.push({ path, code: 'expectedNumber', params: { got: typeof v === 'number' ? String(v) : JSON.stringify(v) ?? String(v) } }); return 0; }
  if (positive && v <= 0) c.push({ path, code: 'mustBePositive', params: { got: String(v) } });
  return v;
}

function pt(c: Ctx, v: unknown, path: string): Pt {
  if (!Array.isArray(v) || v.length !== 2) { c.push({ path, code: 'expectedPoint' }); return [0, 0]; }
  return [num(c, v[0], join(path, 0)), num(c, v[1], join(path, 1))];
}

function oneOf<T extends string>(c: Ctx, v: unknown, path: string, options: readonly T[]): T {
  if (typeof v !== 'string' || !options.includes(v as T)) { c.push({ path, code: 'expectedOneOf', params: { options: options.join('|'), got: String(JSON.stringify(v)) } }); return options[0]!; }
  return v as T;
}

function bool(c: Ctx, v: unknown, path: string): boolean {
  if (typeof v !== 'boolean') { c.push({ path, code: 'expectedBool' }); return false; }
  return v;
}

/* Zero-length segments break direction math (bridge decks, strip rotation) and mean nothing to the author. */
function noRepeats(c: Ctx, pts: Pt[], path: string, closed: boolean) {
  const n = pts.length;
  for (let i = 0; i < (closed ? n : n - 1); i++) {
    const a = pts[i]!, b = pts[(i + 1) % n]!;
    if (a[0] === b[0] && a[1] === b[1]) c.push({ path, code: 'pointsCoincide', params: { i: String(i), j: String((i + 1) % n) } });
  }
}

function polyline(c: Ctx, v: unknown, path: string): Pt[] {
  const pts = list(c, v, path).map((p, i) => pt(c, p, join(path, i)));
  if (Array.isArray(v) && pts.length < 2) c.push({ path, code: 'lineTooShort', params: { n: pts.length } });
  noRepeats(c, pts, path, false);
  return pts;
}

const cross = (o: Pt, a: Pt, b: Pt) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
function segmentsCross(a: Pt, b: Pt, p: Pt, q: Pt) {
  const d1 = cross(p, q, a), d2 = cross(p, q, b), d3 = cross(a, b, p), d4 = cross(a, b, q);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

function polygon(c: Ctx, v: unknown, path: string): Pt[] {
  const pts = list(c, v, path).map((p, i) => pt(c, p, join(path, i)));
  if (!Array.isArray(v)) return pts;
  if (pts.length < 3) { c.push({ path, code: 'polygonTooShort', params: { n: pts.length } }); return pts; }
  noRepeats(c, pts, path, true);
  let area2 = 0;
  for (let i = 0; i < pts.length; i++) area2 += cross([0, 0], pts[i]!, pts[(i + 1) % pts.length]!);
  if (Math.abs(area2) < 1e-6) c.push({ path, code: 'polygonZeroArea' });
  const n = pts.length;
  for (let i = 0; i < n; i++) for (let j = i + 2; j < n; j++) {
    if (i === 0 && j === n - 1) continue;  // adjacent through the closing edge
    if (segmentsCross(pts[i]!, pts[(i + 1) % n]!, pts[j]!, pts[(j + 1) % n]!)) {
      c.push({ path, code: 'polygonEdgesCross', params: { i: String(i), j: String(j) } });
      return pts;
    }
  }
  return pts;
}

function objects(c: Ctx, v: unknown, path: string): MapObjects {
  const o = record(c, v, path, OBJECT_KEYS, OPTIONAL_OBJECT_KEYS) ?? {};
  const each = <T>(key: keyof MapObjects, f: (item: unknown, p: string) => T): T[] =>
    key in o ? list(c, o[key], join(path, key)).map((item, i) => f(item, join(join(path, key), i))) : [];
  const fields = (item: unknown, p: string, keys: readonly string[]) => record(c, item, p, keys) ?? {};
  const patch = (v: unknown, p: string) => {
    const f = fields(v, p, ['x', 'z', 'w', 'd', 'rot']);
    return { x: num(c, f.x, join(p, 'x')), z: num(c, f.z, join(p, 'z')), w: num(c, f.w, join(p, 'w'), true), d: num(c, f.d, join(p, 'd'), true), rot: num(c, f.rot, join(p, 'rot')) };
  };
  return {
    camps: each('camps', (v, p) => pt(c, v, p)),
    bridges: each('bridges', (v, p) => pt(c, v, p)),
    trees: each('trees', (v, p) => pt(c, v, p)),
    rocks: each('rocks', (v, p) => pt(c, v, p)),
    pillars: each('pillars', (v, p) => pt(c, v, p)),
    walls: each('walls', (v, p) => {
      const f = fields(v, p, ['points', 'style']);
      return { points: polyline(c, f.points, join(p, 'points')), style: oneOf(c, f.style, join(p, 'style'), WALL_STYLES) };
    }),
    buildings: each('buildings', (v, p) => {
      const f = fields(v, p, ['x', 'z', 'w', 'd', 'rot', 'style']);
      return {
        x: num(c, f.x, join(p, 'x')), z: num(c, f.z, join(p, 'z')), w: num(c, f.w, join(p, 'w'), true), d: num(c, f.d, join(p, 'd'), true),
        rot: num(c, f.rot, join(p, 'rot')), style: oneOf(c, f.style, join(p, 'style'), BUILDING_STYLES),
      };
    }),
    streams: each('streams', (v, p) => {
      const f = fields(v, p, ['points', 'width']);
      return { points: polyline(c, f.points, join(p, 'points')), width: num(c, f.width, join(p, 'width'), true) };
    }),
    ponds: each('ponds', (v, p) => polygon(c, v, p)),
    cliffs: each('cliffs', (v, p) => {
      const f = fields(v, p, ['points', 'closed']);
      const closed = bool(c, f.closed, join(p, 'closed'));
      return { points: closed ? polygon(c, f.points, join(p, 'points')) : polyline(c, f.points, join(p, 'points')), closed };
    }),
    brush: each('brush', (v, p) => {
      const f = fields(v, p, ['x', 'z', 'rx', 'rz', 'rot']);
      return { x: num(c, f.x, join(p, 'x')), z: num(c, f.z, join(p, 'z')), rx: num(c, f.rx, join(p, 'rx'), true), rz: num(c, f.rz, join(p, 'rz'), true), rot: num(c, f.rot, join(p, 'rot')) };
    }),
    roads: each('roads', (v, p) => {
      const f = record(c, v, p, ['points', 'width', 'kind'], ['kind']) ?? {};
      const road: Road = { points: polyline(c, f.points, join(p, 'points')), width: num(c, f.width, join(p, 'width'), true) };
      if ('kind' in f) road.kind = oneOf(c, f.kind, join(p, 'kind'), ROAD_KINDS);
      return road;
    }),
    decor: each('decor', (v, p) => {
      const f = fields(v, p, ['x', 'z', 'kind', 'rot', 'scale']);
      return {
        x: num(c, f.x, join(p, 'x')), z: num(c, f.z, join(p, 'z')), kind: oneOf(c, f.kind, join(p, 'kind'), DECOR_KINDS),
        rot: num(c, f.rot, join(p, 'rot')), scale: num(c, f.scale, join(p, 'scale'), true),
      };
    }),
    props: each('props', (v, p) => {
      const f = fields(v, p, ['x', 'z', 'kind', 'rot']);
      return { x: num(c, f.x, join(p, 'x')), z: num(c, f.z, join(p, 'z')), kind: oneOf(c, f.kind, join(p, 'kind'), PROP_KINDS), rot: num(c, f.rot, join(p, 'rot')) };
    }),
    fields: each('fields', patch),
    fords: each('fords', patch),
  };
}

function near(a: unknown, b: unknown): boolean {
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) <= SYM_EPS;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((v, i) => near(v, b[i]));
  if (isRecord(a) && isRecord(b)) return Object.keys(a).every(k => near(a[k], b[k]));
  return a === b;
}
const angleNear = (a: number, b: number, period: number) => {
  const d = ((a - b) % period + period) % period;
  return Math.min(d, period - d) <= SYM_EPS;
};

function samePoints(a: Pt[], b: Pt[], closed: boolean): boolean {
  if (a.length !== b.length) return false;
  const rev = [...b].reverse();
  if (!closed) return near(a, b) || near(a, rev);
  const n = a.length, shifted = (arr: Pt[], s: number) => arr.map((_, i) => arr[(i + s) % n]);
  for (let s = 0; s < n; s++) if (near(a, shifted(b, s)) || near(a, shifted(rev, s))) return true;
  return false;
}

/* Whether a centre item matches another's mirror as the same shape: open lines may run either way, closed
   shapes (ponds, closed cliffs) may also start at any vertex. Buildings, fields and fords compare rot modulo
   π — a rectangle turned half-way is itself, so one building on the centre is valid; decor and props compare
   modulo a full turn, since a grave or a cart has a facing. */
export const HALF_TURN_KEYS: readonly (keyof MapObjects)[] = ['buildings', 'fields', 'fords'];
function sameShape(key: keyof MapObjects, a: unknown, b: unknown): boolean {
  if (key === 'ponds') return samePoints(a as Pt[], b as Pt[], true);
  if (!isRecord(a) || !isRecord(b)) return near(a, b);
  const { points: pa, rot: ra, ...restA } = a, { points: pb, rot: rb, ...restB } = b;
  if (!near(restA, restB)) return false;
  if (typeof ra === 'number' && !angleNear(ra, rb as number, HALF_TURN_KEYS.includes(key) ? Math.PI : 2 * Math.PI)) return false;
  return pa === undefined || samePoints(pa as Pt[], pb as Pt[], a.closed === true);
}

function checkCenterSymmetry(c: Ctx, center: MapObjects) {
  const mirrored = mirrorObjects(center);
  for (const key of OBJECT_KEYS) {
    const items = center[key] as unknown[], mirrors = mirrored[key] as unknown[];
    mirrors.forEach((m, i) => {
      if (!items.some(item => sameShape(key, item, m))) c.push({ path: `center.${key}[${i}]`, code: 'centerNotSymmetric' });
    });
  }
}

/* How far past the diagonal an authored object may sit: slack for hand-placed points meant to be on it. */
const HALF_TOL = 0.05;
/* Base furniture (fountain, flag 2.9 m out, shop 2.8 m out) must stay on the map. */
const BASE_MARGIN = 5;
/* A bridge must sit on a stream: the view orients its deck along the stream under it. */
const BRIDGE_REACH = 1;
/* A bridge clears the water within 2.5 m of its centre (layout.ts): a stream wider than this (plus a hero's
   radius on each bank) could not be crossed on it. */
const BRIDGE_SPAN = 4;
/* A ford must reach this far past each bank of the stream it crosses (a hero's radius, rounded up) — less
   and the nav grid would find no walkable cell between water and bank. */
const FORD_BANK = 0.5;
/* ...and be this wide along the stream: two 0.5 m nav cells, or the crossing may fall between cell centres
   and paths detour to the nearest bridge. */
const FORD_MIN_WIDTH = 1;

const anchorOf = (key: keyof MapObjects, item: unknown): Pt => {
  if (Array.isArray(item)) return key === 'ponds' ? centroid(item as Pt[]) : item as Pt;
  const o = item as { x?: number; z?: number; points?: Pt[] };
  return o.points ? centroid(o.points) : [o.x!, o.z!];
};
const centroid = (pts: Pt[]): Pt => [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
const verticesOf = (key: keyof MapObjects, item: unknown): Pt[] =>
  key === 'ponds' ? item as Pt[] : isRecord(item) && item.points ? item.points as Pt[] : [anchorOf(key, item)];

/* Placement rules, checked once the structure is sound:
   - point-like objects (and a building's or decor's centre) lie inside the map; lines and polygons need at
     least one vertex inside (a stream may flow off the edge);
   - authored objects lie on the author's half: point-like ones by position, lines and polygons by the
     centroid of their vertices (they may cross the diagonal);
   - no two objects of a kind are identical, counting the mirrored half;
   - bridges sit on a stream; required objects keep clear of each other. */
function checkPlacement(c: Ctx, map: MapData) {
  const hw = map.width / 2, hh = map.height / 2;
  const onMap = ([x, z]: Pt) => Math.abs(x) <= hw && Math.abs(z) <= hh;
  const outside = { code: 'outsideMap', params: { hw: String(hw), hh: String(hh) } } as const;

  if (Math.abs(map.base[0]) > hw - BASE_MARGIN || Math.abs(map.base[1]) > hh - BASE_MARGIN) {
    c.push({ path: 'base', code: 'baseNearEdge', params: { hw: String(hw - BASE_MARGIN), hh: String(hh - BASE_MARGIN) } });
  }
  for (const [path, p] of [['points.side', map.points.side], ['den.pos', map.den.pos]] as const) {
    if (!onMap(p)) c.push({ path, ...outside });
    else if (!inAuthoredHalf(map, p, HALF_TOL)) c.push({ path, code: 'mirroredHalfPoint' });
  }

  for (const part of ['half', 'center'] as const) {
    for (const key of OBJECT_KEYS) {
      (map[part][key] as unknown[]).forEach((item, i) => {
        const path = `${part}.${key}[${i}]`, pts = verticesOf(key, item);
        if (pts.length === 1 ? !onMap(pts[0]!) : !pts.some(onMap)) c.push(pts.length === 1 ? { path, ...outside } : { path, code: 'noVertexInside' });
        else if (part === 'half' && !inAuthoredHalf(map, anchorOf(key, item), HALF_TOL)) {
          c.push({ path, code: pts.length > 1 ? 'mirroredHalfCentroid' : 'mirroredHalf' });
        }
      });
    }
  }

  const e = expandMap(map);
  for (const key of OBJECT_KEYS) {
    type Label = { path: string; mirror: boolean };
    const labels: Label[] = [
      ...map.center[key].map((_, i) => ({ path: `center.${key}[${i}]`, mirror: false })),
      ...map.half[key].map((_, i) => ({ path: `half.${key}[${i}]`, mirror: false })),
      ...map.half[key].map((_, i) => ({ path: `half.${key}[${i}]`, mirror: true })),
    ];
    const seen = new Map<string, Label>();
    (e.objects[key] as unknown[]).forEach((item, i) => {
      const k = JSON.stringify(item), first = seen.get(k), label = labels[i]!;
      if (first === undefined) seen.set(k, label);
      // a mirror matching a mirror only repeats a duplicate already reported on the authored side
      else if (!(label.mirror && first.mirror)) {
        c.push({ path: label.path, code: label.mirror ? 'mirrorDuplicates' : first.mirror ? 'duplicatesMirrorOf' : 'duplicates', params: { first: first.path } });
      }
    });
  }

  for (const part of ['half', 'center'] as const) {
    map[part].bridges.forEach(([x, z], i) => {
      const under = e.objects.streams.filter(s => distPolyline(x, z, s.points) <= BRIDGE_REACH);
      if (!under.length) c.push({ path: `${part}.bridges[${i}]`, code: 'bridgeNotOnStream', params: { reach: BRIDGE_REACH } });
      else if (under.some(s => s.width > BRIDGE_SPAN)) c.push({ path: `${part}.bridges[${i}]`, code: 'bridgeStreamTooWide', params: { span: BRIDGE_SPAN } });
    });
  }

  for (const part of ['half', 'center'] as const) {
    map[part].fords.forEach((f, i) => {
      const issue = fordIssue(f, e.objects.streams);
      if (issue) c.push({ path: `${part}.fords[${i}]`, ...issue });
    });
  }

  const gap = Math.max(10, 2 * map.pointRadius);
  // [place, its path in the file (null: a mirrored copy, which has none), position]
  const required: [MapPlace, string | null, Pt][] = [
    ['base', 'base', e.bases.a], ['baseMirror', null, e.bases.b],
    ['pointsSide', 'points.side', e.points[0]!.pos], ['pointsCenter', 'points.center', e.points[1]!.pos], ['pointsSideMirror', null, e.points[2]!.pos],
    ['denPos', 'den.pos', e.dens[0]!.pos], ['denMirror', null, e.dens[1]!.pos],
  ];
  for (let i = 0; i < required.length; i++) for (let j = i + 1; j < required.length; j++) {
    const [, pathA, a] = required[i]!, [nb, , b] = required[j]!, d = Math.hypot(a[0] - b[0], a[1] - b[1]);
    if (d < gap && pathA) c.push({ path: pathA, code: 'tooClose', params: { place: nb, d: d.toFixed(1), gap: String(gap) } });
  }
}

/* A ford is valid when a stream's centre line passes through its rectangle and, where the line comes
   closest to the ford's centre, the rectangle covers the water bank to bank (plus FORD_BANK each side). */
function fordIssue(f: Patch, streams: Stream[]): Omit<MapIssue, 'path'> | null {
  const c = Math.cos(f.rot), s = Math.sin(f.rot);
  const inside = ([x, z]: Pt, tol = 1e-6) => {
    const dx = x - f.x, dz = z - f.z;
    return Math.abs(dx * c + dz * s) <= f.w / 2 + tol && Math.abs(-dx * s + dz * c) <= f.d / 2 + tol;
  };
  let best: { p: Pt; u: Pt; n: Pt; width: number; d: number } | null = null;
  for (const st of streams) {
    for (let i = 0; i < st.points.length - 1; i++) {
      const a = st.points[i]!, b = st.points[i + 1]!, vx = b[0] - a[0], vz = b[1] - a[1], len = Math.hypot(vx, vz);
      const t = Math.max(0, Math.min(1, ((f.x - a[0]) * vx + (f.z - a[1]) * vz) / (len * len)));
      const p: Pt = [a[0] + vx * t, a[1] + vz * t], d = Math.hypot(f.x - p[0], f.z - p[1]);
      if (!best || d < best.d) best = { p, u: [vx / len, vz / len], n: [-vz / len, vx / len], width: st.width, d };
    }
  }
  if (!best || !inside(best.p)) return { code: 'fordMissesStream' };
  const reach = best.width / 2 + FORD_BANK, { p, u, n } = best;
  const at = (across: number, along: number): Pt => [p[0] + n[0] * across + u[0] * along, p[1] + n[1] * across + u[1] * along];
  if (![at(reach, 0), at(-reach, 0)].every(q => inside(q))) return { code: 'fordTooShort', params: { need: (2 * reach).toFixed(1) } };
  const half = FORD_MIN_WIDTH / 2;
  if (![at(reach, half), at(reach, -half), at(-reach, half), at(-reach, -half)].every(q => inside(q))) return { code: 'fordTooNarrow', params: { need: String(FORD_MIN_WIDTH) } };
  return null;
}

/** Validates untrusted parsed JSON into a MapData, reporting every problem at once (path + reason). */
export function validateMap(raw: unknown, source = 'map'): MapData {
  const c: Ctx = [];
  const r = record(c, raw, '', ['format', 'name', 'width', 'height', 'pointRadius', 'base', 'points', 'den', 'half', 'center']);
  if (!r) throw new MapValidationError(source, c);
  if (r.format !== 1) c.push({ path: 'format', code: 'unsupportedFormat', params: { got: String(JSON.stringify(r.format)) } });
  if (typeof r.name !== 'string' || !r.name.trim()) c.push({ path: 'name', code: 'expectedName' });
  const width = num(c, r.width, 'width', true), height = num(c, r.height, 'height', true);
  for (const [field, v] of [['width', width], ['height', height]] as const) {
    const { min, max } = MAP_SIZE[field];
    if (v > 0 && (v < min || v > max)) c.push({ path: field, code: 'sizeOutOfRange', params: { min: String(min), max: String(max), got: String(v) } });
  }
  const pointRadius = num(c, r.pointRadius, 'pointRadius', true);
  const base = pt(c, r.base, 'base');
  const pr = record(c, r.points, 'points', ['side', 'center']) ?? {};
  const side = pt(c, pr.side, 'points.side'), center = pt(c, pr.center, 'points.center');
  if ('center' in pr && (center[0] !== 0 || center[1] !== 0)) c.push({ path: 'points.center', code: 'centerPointNotOrigin' });
  const dr = record(c, r.den, 'den', ['kind', 'pos']) ?? {};
  const den: Den = { kind: oneOf(c, dr.kind, 'den.kind', DEN_KINDS), pos: pt(c, dr.pos, 'den.pos') };
  const half = objects(c, r.half, 'half');
  const centerObjects = objects(c, r.center, 'center');
  if (c.length) throw new MapValidationError(source, c);

  const map: MapData = { format: 1, name: r.name as string, width, height, pointRadius, base, points: { side, center }, den, half, center: centerObjects };
  checkCenterSymmetry(c, centerObjects);
  checkPlacement(c, map);
  if (c.length) throw new MapValidationError(source, c);
  return map;
}
